export type ThemeMode = 'light' | 'dark';

export interface AppTheme {
  mode: ThemeMode;
  colors: {
    background: string;
    surface: string;
    surfaceMuted: string;
    surfaceHover: string;
    headerBg: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    borderStrong: string;
    primary: string;
    primaryHover: string;
    primarySoft: string;
    onPrimary: string;
    accent: string;
    accentGlow: string;
    accentGlowStrong: string;
    success: string;
    successSoft: string;
    danger: string;
    dangerSoft: string;
    warning: string;
    warningSoft: string;
    info: string;
    infoSoft: string;
  };
  board: {
    todo: string;
    progress: string;
    done: string;
  };
  fonts: {
    sans: string;
    serif: string;
    mono: string;
  };
  fontWeights: {
    light: number;
    medium: number;
    bold: number;
  };
  radius: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    pill: string;
  };
  shadow: {
    sm: string;
    md: string;
    lg: string;
  };
  space: (n: number) => string;
}

const fonts = {
  sans: "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif",
  serif: "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, sans-serif",
  mono: "'SFMono-Regular', Consolas, 'Liberation Mono', monospace",
};

const fontWeights = {
  light: 300,
  medium: 500,
  bold: 700,
};

const radius = {
  sm: '8px',
  md: '10px',
  lg: '16px',
  xl: '20px',
  pill: '999px',
};

const board = {
  todo: '#2563eb',
  progress: '#0ea5e9',
  done: '#059669',
};

export const lightTheme: AppTheme = {
  mode: 'light',
  colors: {
    background: '#f6f7fb',
    surface: '#ffffff',
    surfaceMuted: '#f2f4f8',
    surfaceHover: '#edf1f7',
    headerBg: 'rgba(255, 255, 255, 0.92)',
    textPrimary: '#182338',
    textSecondary: '#526078',
    textMuted: '#627088',
    border: '#e1e6ef',
    borderStrong: '#9ba8ba',
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    primarySoft: '#eaf0ff',
    onPrimary: '#f8fafc',
    accent: '#0ea5e9',
    accentGlow: 'rgba(37, 99, 235, 0.12)',
    accentGlowStrong: 'rgba(37, 99, 235, 0.22)',
    success: '#06734f',
    successSoft: 'rgba(5, 150, 105, 0.12)',
    danger: '#bd1f2d',
    dangerSoft: 'rgba(220, 38, 38, 0.1)',
    warning: '#9a5806',
    warningSoft: 'rgba(217, 119, 6, 0.12)',
    info: '#2563eb',
    infoSoft: 'rgba(59, 130, 246, 0.12)',
  },
  board,
  fonts,
  fontWeights,
  radius,
  shadow: {
    sm: '0 2px 4px rgba(15, 23, 42, 0.025)',
    md: '0 8px 28px rgba(15, 23, 42, 0.06)',
    lg: '0 18px 48px rgba(15, 23, 42, 0.12)',
  },
  space: (n: number) => `${n * 4}px`,
};

export const darkTheme: AppTheme = {
  mode: 'dark',
  colors: {
    background: '#0e1420',
    surface: '#171f2e',
    surfaceMuted: '#1d2738',
    surfaceHover: '#263349',
    headerBg: 'rgba(17, 24, 39, 0.92)',
    textPrimary: '#f8fafc',
    textSecondary: '#b7c3d7',
    textMuted: '#9daec6',
    border: '#2b3649',
    borderStrong: '#65758f',
    primary: '#8aafff',
    primaryHover: '#a8c3ff',
    primarySoft: '#233453',
    onPrimary: '#0f172a',
    accent: '#38bdf8',
    accentGlow: 'rgba(59, 130, 246, 0.18)',
    accentGlowStrong: 'rgba(59, 130, 246, 0.32)',
    success: '#34d399',
    successSoft: 'rgba(52, 211, 153, 0.12)',
    danger: '#f87171',
    dangerSoft: 'rgba(248, 113, 113, 0.12)',
    warning: '#fbbf24',
    warningSoft: 'rgba(251, 191, 36, 0.12)',
    info: '#60a5fa',
    infoSoft: 'rgba(96, 165, 250, 0.12)',
  },
  board,
  fonts,
  fontWeights,
  radius,
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.35)',
    md: '0 12px 32px rgba(0, 0, 0, 0.45)',
    lg: '0 24px 56px rgba(0, 0, 0, 0.55)',
  },
  space: (n: number) => `${n * 4}px`,
};

export const themes: Record<ThemeMode, AppTheme> = {
  dark: darkTheme,
  light: lightTheme,
};

/** @deprecated Prefer `themes.light` / `useAppTheme()` */
export const theme = lightTheme;
